import { useRef, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow } from '../../../components/confirm';
import { parseCsv } from '../../../utils/csv';
import { importMessages, type MessageImportResult } from './api';
import { MESSAGE_FORM_NAME } from './messageLibraryConfig';
import { hasRequiredColumn, previewImport, REQUIRED_COLUMN, type ImportPreview } from './messageImport';
import type { MessageRow } from './types';
import './messageLibrary.css';

interface Props {
  open: boolean;
  /** The loaded rows, matched against the file to build the preview. */
  rows: MessageRow[];
  onClose: () => void;
  /** Called after a successful import so the list reloads. */
  onImported: () => void;
}

/**
 * Bulk Import of reviewed wording.
 *
 * The flow is deliberately preview-then-apply: an operator picks a file, sees
 * exactly what it would change and what it would SKIP, and only then commits.
 * A bulk edit that reports "done" without saying how many rows it quietly
 * ignored is how a spreadsheet typo becomes a silent no-op.
 *
 * Only wording is importable — MSID and MessageKey identify the row and are
 * never written back, because a spreadsheet round-trip is the easiest way to
 * corrupt an identifier every call site depends on. The server enforces the same
 * rules; this dialog only shows them in advance.
 */
export function ImportMessagesDialog({ open, rows, onClose, onImported }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const fileRef = useRef<HTMLInputElement>(null);

  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<ImportPreview | null>(null);

  const reset = () => {
    setFileName('');
    setPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setFileName(file.name);

    const parsed = parseCsv(await file.text());
    if (parsed.length === 0) {
      setPreview(null);
      notify.warning('That file has no data rows.');
      return;
    }
    if (!hasRequiredColumn(parsed)) {
      setPreview(null);
      notify.error(`The file needs a ${REQUIRED_COLUMN} column. Download the Template and try again.`);
      return;
    }
    setPreview(previewImport(parsed, rows));
  };

  const apply = async () => {
    if (!preview || preview.changes.length === 0) return;

    // The shared save flow: confirm → reason gate → audited call → acknowledgement.
    // The reason reaches every audit row via X-Audit-Reason.
    let result: MessageImportResult | null = null;

    await runSave({
      formName: MESSAGE_FORM_NAME,
      auditAction: 'edit',
      confirmTitle: 'Import',
      confirmMessage: `Apply wording changes to ${preview.changes.length} message${preview.changes.length === 1 ? '' : 's'}?`,
      save: async () => { result = await importMessages(preview.changes); },
      // A FUNCTION, so it is resolved AFTER the save and can report what the
      // server actually did — and say so when it skipped rows, rather than
      // claiming success over a file that half applied.
      successMessage: () => {
        const r = result as MessageImportResult | null;
        if (!r) return undefined;
        const skipped = r.unknownKeys + r.emptyMessages;
        const base = `Imported ${r.updated} message${r.updated === 1 ? '' : 's'} successfully.`;
        return skipped > 0 ? `${base} ${skipped} row${skipped === 1 ? '' : 's'} skipped.` : base;
      },
      onSaved: () => {
        reset();
        onClose();
        onImported();
      },
    });
  };

  const stat = (label: string, value: number, tone?: string) => (
    <tr>
      <td>{label}</td>
      <td className={`ml-imp-n${tone ? ` ${tone}` : ''}`}>{value}</td>
    </tr>
  );

  return (
    <AppDialog
      open={open}
      title="Import Messages"
      icon="ti-file-import"
      width={560}
      onClose={() => { reset(); onClose(); }}
      footer={
        <>
          <button
            type="button"
            className="ca-btn ca-btn-solid"
            disabled={!preview || preview.changes.length === 0}
            onClick={() => void apply()}
          >
            Import
          </button>
          <button type="button" className="ca-btn" onClick={() => { reset(); onClose(); }}>
            Cancel
          </button>
        </>
      }
    >
      <div className="ml-imp">
        <p className="ml-imp-help">
          Download <b>Template</b>, edit the wording in Excel, then import the CSV.
          Columns: <b>MSID, MessageKey, Message, Remark</b>. Rows are matched on
          {' '}<b>MessageKey</b> (case-insensitive). MSID and keys are never changed by an
          import, and unknown keys are skipped.
        </p>

        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          aria-label="Import file"
          onChange={(e) => void onPick(e.target.files?.[0])}
        />

        {preview && (
          <>
            <table className="ml-imp-stats">
              <tbody>
                {stat('Rows in file', preview.total)}
                {stat('Messages changed', preview.changes.length, 'is-go')}
                {preview.unchanged > 0 && stat('Already up to date', preview.unchanged)}
                {stat('Unknown keys (skipped)', preview.unknownKeys.length,
                      preview.unknownKeys.length ? 'is-warn' : undefined)}
                {stat('Empty Message (skipped)', preview.emptyMessages.length,
                      preview.emptyMessages.length ? 'is-warn' : undefined)}
              </tbody>
            </table>

            {/* Name the skipped keys rather than only counting them — a count
                alone leaves the operator guessing which rows to fix. */}
            {preview.unknownKeys.length > 0 && (
              <p className="ml-imp-skips">
                <b>Not found:</b> {preview.unknownKeys.slice(0, 8).join(', ')}
                {preview.unknownKeys.length > 8 && ` … +${preview.unknownKeys.length - 8} more`}
              </p>
            )}
            {preview.emptyMessages.length > 0 && (
              <p className="ml-imp-skips">
                <b>Blank Message:</b> {preview.emptyMessages.slice(0, 8).join(', ')}
                {preview.emptyMessages.length > 8 && ` … +${preview.emptyMessages.length - 8} more`}
              </p>
            )}

            {preview.changes.length === 0 && (
              <p className="ml-imp-none">
                Nothing to import from {fileName || 'this file'}.
              </p>
            )}
          </>
        )}
      </div>
    </AppDialog>
  );
}

export default ImportMessagesDialog;
